/**
 * Centralized logging utility — canonical types, formatting, and server logger.
 *
 * Shared types (LogLevel, LogContext) and the `formatLogMessage` helper are
 * re-exported so `logger-client.ts` (browser) can import them instead of
 * duplicating definitions.
 */

// ---------------------------------------------------------------------------
// Shared types & formatting (used by both server and client loggers)
// ---------------------------------------------------------------------------

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogContext {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- LogContext is intentionally open for ad-hoc structured metadata
  [key: string]: any
}

/** Canonical log-line formatter shared across server + client loggers. */
export function formatLogMessage(level: LogLevel, message: string, context?: LogContext): string {
  const timestamp = new Date().toISOString()
  const contextStr = context ? ` ${JSON.stringify(context)}` : ''
  return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`
}

// ---------------------------------------------------------------------------
// Server-side logger
// ---------------------------------------------------------------------------

class Logger {
  // Works in both Node.js and browser (Vite)
  private isDevelopment = typeof import.meta !== 'undefined' && import.meta.env?.DEV || process.env.NODE_ENV === 'development'
  private isProduction = typeof import.meta !== 'undefined' && import.meta.env?.PROD || process.env.NODE_ENV === 'production'

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    return formatLogMessage(level, message, context)
  }

  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.debug(this.formatMessage('debug', message, context))
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.info(this.formatMessage('info', message, context))
    }
  }

  warn(message: string, context?: LogContext): void {
    console.warn(this.formatMessage('warn', message, context))

    // In production, could send to monitoring service
    if (this.isProduction) {
      // Send to Sentry, LogRocket, etc.
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    const errorContext = {
      ...context,
      error: error ? {
        message: error.message,
        stack: error.stack,
        name: error.name,
      } : undefined,
    }

    console.error(this.formatMessage('error', message, errorContext))

    // In production, send to error tracking service
    if (this.isProduction) {
      // Send to Sentry, LogRocket, etc.
      // Example: Sentry.captureException(error, { extra: context })
    }
  }

  // Group related logs
  group(label: string): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.group(label)
    }
  }

  groupEnd(): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.groupEnd()
    }
  }

  // Table for structured data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- console.table accepts any structured data
  table(data: any): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.table(data)
    }
  }
}

export const logger = new Logger()